import { Box, Divider, Modal, Typography } from "@mui/material";
import {
  useCreatePartOtherProcessV2,
  useUpdatePartOtherProcessV2,
  useDeletePartOtherProcessV2,
  useGetBreakdownProcessV2,
} from "@/hooks/breakdownHooks";
import CloseButton from "@/components/buttons/CloseButton";
import { InformationModal } from "@/styles/globalstyles";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import EditableProcessList from "./EditableProcessList";
import { useState } from "react";
import {
  useGetOEMPartOtherProcesses,
  useGetSparePartOtherProcesses,
} from "@/hooks/masterHook";
import { type Master } from "@/api/masterService";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";

interface ManagePartOtherProcessModalProps {
  open: boolean;
  handleClose: () => void;
  partId?: number;
  canEdit: boolean;
}

const ManagePartOtherProcessModal = ({
  open,
  handleClose,
  partId,
  canEdit,
}: ManagePartOtherProcessModalProps) => {
  const [disabled, setDisabled] = useState(false);
  const {
    data: breakdownProcessV2,
    isLoading: isLoadingBreakdownProcessV2,
    isError: isErrorBreakdownProcessV2,
  } = useGetBreakdownProcessV2(partId || 0);

  const {
    data: OEMPartOtherProcesses,
    isLoading: isLoadingOEMPartOtherProcesses,
    isError: isErrorOEMPartOtherProcesses,
  } = useGetOEMPartOtherProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    data: sparePartOtherProcesses,
    isLoading: isLoadingSparePartOtherProcesses,
    isError: isErrorSparePartOtherProcesses,
  } = useGetSparePartOtherProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    mutate: createPartOtherProcessV2,
    isPending: isCreatingPartOtherProcessV2,
  } = useCreatePartOtherProcessV2();

  const {
    mutate: updatePartOtherProcessV2,
    isPending: isUpdatingPartOtherProcessV2,
  } = useUpdatePartOtherProcessV2();

  const {
    mutate: deletePartOtherProcessV2,
    isPending: isDeletingPartOtherProcessV2,
  } = useDeletePartOtherProcessV2();

  useQueryHandler({
    isFetching:
      isLoadingBreakdownProcessV2 ||
      isLoadingOEMPartOtherProcesses ||
      isLoadingSparePartOtherProcesses ||
      isCreatingPartOtherProcessV2 ||
      isUpdatingPartOtherProcessV2 ||
      isDeletingPartOtherProcessV2,
    isError:
      isErrorBreakdownProcessV2 ||
      isErrorOEMPartOtherProcesses ||
      isErrorSparePartOtherProcesses,
  });

  const onClose = () => {
    handleClose();
    setDisabled(false);
  };

  const handleAddOEMOtherProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createPartOtherProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_other_processes: [
            {
              ms_oem_other_process_v2_id: process.master_process_id,
            },
          ],
          spare_other_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating OEM part other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleAddSpareOtherProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createPartOtherProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_other_processes: [],
          spare_other_processes: [
            {
              ms_spare_other_process_v2_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating spare part other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditOEMOtherProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updatePartOtherProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_other_processes: [
            {
              part_oem_other_process_id: process.id,
              ms_oem_other_process_v2_id: process.master_process_id,
            },
          ],
          spare_other_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating OEM other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditSpareOtherProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updatePartOtherProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_other_processes: [],
          spare_other_processes: [
            {
              part_spare_other_process_id: process.id,
              ms_spare_other_process_v2_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating spare other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteOEMOtherProcess = async (processId: number) => {
    deletePartOtherProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_other_processes: [{ part_oem_other_process_id: processId }],
          spare_other_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting oem other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteSpareOtherProcess = async (processId: number) => {
    deletePartOtherProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_other_processes: [],
          spare_other_processes: [{ part_spare_other_process_id: processId }],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting spare other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...InformationModal,
          minHeight: "400px",
          maxHeight: "90vh",
          width: "70vw",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h2">{"จัดการ Part Other Process"}</Typography>
        <Divider sx={{ my: 1 }} />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} p={1} pt={2}>
          <EditableProcessList
            title="OEM Part Other Process"
            data={
              breakdownProcessV2?.oem_other_process?.map((process) => ({
                id: process.id,
                name: process.name,
                master_process_id: process.master_process_id || 0,
              })) || []
            }
            options={
              OEMPartOtherProcesses?.map((process: Master) => ({
                value: process.id,
                label: process.name,
              })) || []
            }
            handleAddProcess={async (process) => {
              handleAddOEMOtherProcess(process);
            }}
            handleEditProcess={async (process) => {
              handleEditOEMOtherProcess(process);
            }}
            handleDeleteProcess={async (processId) => {
              handleDeleteOEMOtherProcess(processId);
            }}
            disabled={disabled || !canEdit}
            setDisabled={setDisabled}
          />
          <EditableProcessList
            title="Spare Part Other Process"
            data={
              breakdownProcessV2?.spare_other_process?.map((process) => ({
                id: process.id,
                name: process.name,
                master_process_id: process.master_process_id || 0,
              })) || []
            }
            options={
              sparePartOtherProcesses?.map((process: Master) => ({
                value: process.id,
                label: process.name,
              })) || []
            }
            handleAddProcess={async (process) => {
              handleAddSpareOtherProcess(process);
            }}
            handleEditProcess={async (process) => {
              handleEditSpareOtherProcess(process);
            }}
            handleDeleteProcess={async (processId) => {
              handleDeleteSpareOtherProcess(processId);
            }}
            disabled={disabled || !canEdit}
            setDisabled={setDisabled}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ManagePartOtherProcessModal;
